import { useMemo, useState, type ReactNode } from "react";
import { useOptionalAppConfig } from "../../../services/studio/AppConfigProvider";
import type { CalAttendee, CalEvent, Self } from "../types";
import { addAttendeeChange, removeAttendeeChange, type CalendarChange } from "../domain/changes";
import { busyDuring, type Schedule } from "../domain/freeBusy";
import { domainOf, isExternal } from "../domain/people";
import type { LoadSchedules } from "../hooks/useAvailability";
import { useDesignations } from "../hooks/useDesignations";
import { AttendeeInput, initials } from "./AttendeeInput";
import { CloseSmIcon } from "./CalendarIcons";
import styles from "./CalendarModule.module.css";

/** Longer attendee lists collapse behind "Show all". */
const ATTENDEES_SHOWN = 8;

const RESPONSE_BADGE: Record<CalAttendee["response"], [string, string]> = {
  yes: ["Accepted", styles.badgeOk!],
  may: ["Tentative", styles.badgeWarn!],
  no: ["Declined", styles.badgeErr!],
  wait: ["No Reply", styles.badgeNeutral!],
};

interface MeetingAttendeesProps {
  readonly event: CalEvent;
  readonly self: Self;
  /** Everyone except you, the organiser and rooms; required first. */
  readonly others: readonly CalAttendee[];
  /** Free/busy at the meeting's time for the people on it; null while loading. */
  readonly availability: ReadonlyMap<string, Schedule> | null;
  /** Organiser with write permission: attendees can be added and removed. */
  readonly canEdit: boolean;
  readonly token: string | null;
  readonly loadSchedules: LoadSchedules;
  readonly onStage: (change: CalendarChange) => void;
}

/** The Attendees section: who is invited and their replies, and, for the organiser, add and remove. */
export function MeetingAttendees({
  event,
  self,
  others,
  availability,
  canEdit,
  token,
  loadSchedules,
  onStage,
}: Readonly<MeetingAttendeesProps>) {
  const [showAll, setShowAll] = useState(false);
  const designations = useDesignations(token, others.map((a) => a.email));
  // Someone added from outside the organisation gets a warning Undo toast, unless the Studio "External recipient
  // warning" is off. Same rule as the picker's External label: not on any of your addresses' domains.
  const warnExternal = useOptionalAppConfig()?.data?.externalRecipientWarnEnabled ?? true;
  const ownDomains = useMemo(() => new Set([...self.emails].map(domainOf).filter(Boolean)), [self.emails]);
  const busy = (email: string) => (availability ? busyDuring(availability, email, event, true) === true : false);
  const busyLine = <small className={styles.busyNote}>Busy at this time</small>;
  /** The line under a name: their designation when the directory gives one (as in the mockup), else their email address. */
  const detailLine = (a: CalAttendee) => {
    const designation = designations.get(a.email) ?? null;
    let text = designation ?? a.email;
    if (a.type === "optional") text = designation ? `Optional · ${designation}` : "Optional";
    return <small title={designation ? `${designation} · ${a.email}` : a.email}>{text}</small>;
  };

  const rows: ReactNode[] = [
    <div key="you" className={styles.attRow}>
      <span className={`${styles.av} ${styles.avYou}`}>{initials(self.name || "You")}</span>
      <span className={styles.attName}>You</span>
      {event.isOrganizer ? (
        <span className={`${styles.badge} ${styles.badgeOk}`}>Organiser</span>
      ) : (
        <span className={`${styles.badge} ${RESPONSE_BADGE[event.response === "organizer" ? "yes" : event.response][1]}`}>
          {RESPONSE_BADGE[event.response === "organizer" ? "yes" : event.response][0]}
        </span>
      )}
    </div>,
  ];
  if (!event.isOrganizer) {
    rows.push(
      <div key="organiser" className={styles.attRow}>
        <span className={styles.av}>{initials(event.organizerName)}</span>
        <span className={styles.attName}>
          {event.organizerName}
          {busy(event.organizerEmail) ? busyLine : <small>Organiser</small>}
        </span>
      </div>,
    );
  }
  const visible = showAll ? others : others.slice(0, ATTENDEES_SHOWN);
  for (const a of visible) {
    rows.push(
      <div key={a.email} className={styles.attRow}>
        <span className={styles.av}>{initials(a.name)}</span>
        <span className={styles.attName}>
          {a.name}
          {busy(a.email) ? busyLine : detailLine(a)}
        </span>
        {/* Other people's replies are only reliable on the organiser's copy of the meeting. */}
        {event.isOrganizer && (
          <span className={`${styles.badge} ${RESPONSE_BADGE[a.response][1]}`}>{RESPONSE_BADGE[a.response][0]}</span>
        )}
        {canEdit && (
          <button
            type="button"
            className={styles.attRemove}
            onClick={() => onStage(removeAttendeeChange(event, a.email))}
            aria-label={`Remove ${a.name}`}
            title={`Remove ${a.name}`}
          >
            <CloseSmIcon />
          </button>
        )}
      </div>,
    );
  }

  const exclude = new Set([...self.emails, event.organizerEmail, ...event.attendees.map((a) => a.email)]);

  return (
    <div className={styles.sec}>
      <div className={styles.secH}>
        <span className={styles.secBar} />
        <span className={styles.secTitle}>Attendees</span>
        <span className={`${styles.secRight} ${styles.secMeta}`}>{others.length + (event.isOrganizer ? 1 : 2)}</span>
      </div>
      <div className={styles.att}>{rows}</div>
      {!showAll && others.length > ATTENDEES_SHOWN && (
        <button type="button" className={styles.newLink} onClick={() => setShowAll(true)}>
          Show all {others.length}
        </button>
      )}
      {canEdit && (
        <AttendeeInput
          when={event}
          token={token}
          selfEmails={self.emails}
          exclude={exclude}
          loadSchedules={loadSchedules}
          onPick={(person, availability) =>
            onStage(addAttendeeChange(event, person, availability, warnExternal && isExternal(person.email, ownDomains)))
          }
          placeholder="Add attendee — type a name"
        />
      )}
    </div>
  );
}
